"use strict";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { TOPAZ_SCW_CONNECTOR_ID } from "components/Web3Provider/privy/topazScwConnector";
import { CONNECTION_PROVIDER_IDS } from "constants/web3";
import { useConnector } from "contexts/UniswapContext";
import { useFeatureFlaggedChainIds } from "features/chains/hooks/useFeatureFlaggedChainIds";
import { useOrderedChainIds } from "features/chains/hooks/useOrderedChainIds";
import { ALL_CHAIN_IDS, UniverseChainId } from "features/chains/types";
import { getEnabledChains, isTestnetChain } from "features/chains/utils";
import { selectIsTestnetModeEnabled } from "features/settings/selectors";
import { isTestEnv } from "utilities/src/environment/env";
import { logger } from "utilities/src/logger/logger";
import { isInterface } from "utilities/src/platform";
function useConnectorWithCatch() {
  try {
    return useConnector();
  } catch (e) {
    if (isInterface && !isTestEnv()) {
      logger.error(e, {
        tags: { file: "src/features/settings/hooks", function: "useConnectorWithCatch" }
      });
    }
    return void 0;
  }
}
function getConnectorSupportedChains(connector) {
  if (connector?.id === TOPAZ_SCW_CONNECTOR_ID) {
    return [UniverseChainId.Mainnet];
  }
  switch (connector?.type) {
    case CONNECTION_PROVIDER_IDS.WALLET_CONNECT_CONNECTOR_ID:
      return connector.getNamespaceChainsIds?.().length ? connector.getNamespaceChainsIds?.() : ALL_CHAIN_IDS;
    default:
      return ALL_CHAIN_IDS;
  }
}
function useConnectedWalletSupportedChains() {
  const connector = useConnectorWithCatch();
  return useMemo(() => getConnectorSupportedChains(connector), [connector]);
}
export function useIsModeMismatch(chainId) {
  const { isTestnetModeEnabled } = useEnabledChains();
  return isTestnetChain(chainId ?? UniverseChainId.Mainnet) ? !isTestnetModeEnabled : isTestnetModeEnabled;
}
export function useEnabledChains() {
  const featureFlaggedChainIds = useFeatureFlaggedChainIds();
  const connectedWalletChainIds = useConnectedWalletSupportedChains();
  const isTestnetModeEnabled = useSelector(selectIsTestnetModeEnabled);
  const {
    chains: unorderedChains,
    gqlChains,
    defaultChainId
  } = useMemo(
    () => getEnabledChains({
      isTestnetModeEnabled,
      connectedWalletChainIds,
      featureFlaggedChainIds
    }),
    [isTestnetModeEnabled, connectedWalletChainIds, featureFlaggedChainIds]
  );
  const orderedChains = useOrderedChainIds(unorderedChains);
  return useMemo(() => {
    return { chains: orderedChains, gqlChains, defaultChainId, isTestnetModeEnabled };
  }, [defaultChainId, gqlChains, isTestnetModeEnabled, orderedChains]);
}
export function useEnabledChainsWithConnector(connector) {
  const featureFlaggedChainIds = useFeatureFlaggedChainIds();
  const connectedWalletChainIds = useMemo(() => getConnectorSupportedChains(connector), [connector]);
  const isTestnetModeEnabled = useSelector(selectIsTestnetModeEnabled);
  return useMemo(
    () => getEnabledChains({ isTestnetModeEnabled, connectedWalletChainIds, featureFlaggedChainIds }),
    [isTestnetModeEnabled, connectedWalletChainIds, featureFlaggedChainIds]
  );
}
